import React, { useCallback, useRef, useState } from 'react'
import { View, ActivityIndicator } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Icon, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { api } from '@api'
import { isPlanLimitError, showSuccessToast, translateApiError } from '@utils'
import { createGhostProfile } from '@hooks'
import {
  CanonicalField,
  identityKeys,
  ParsedFile,
  parseContactsFile,
  rowToGhostProfile,
  setMappingAt,
  UNSUPPORTED_FORMAT_ERROR,
} from './parseContactsFile'

// "Import from a CSV file" block for the web import screen. The user drops a
// .csv export; we auto-detect the columns, let them check and correct that
// reading on a preview, then create one ghost profile per row that is not
// already among their contacts.
// CSV-only: the previous Excel (.xlsx) path relied on the `xlsx`/SheetJS library
// which is vulnerable to CVE-2023-30533 / CVE-2024-22363 with no patched build,
// so binary uploads are now rejected.

type Phase = 'idle' | 'parsed' | 'importing' | 'done'

// Human labels (FR fallback) used for the "detected columns" summary chips.
const FIELD_LABEL_KEYS: Record<CanonicalField, string> = {
  firstName: 'importContacts.firstName',
  lastName: 'importContacts.lastName',
  fullName: 'importContacts.file.fullName',
  email: 'importContacts.email',
  phone: 'importContacts.phone',
  company: 'importContacts.company',
  jobTitle: 'importContacts.profession',
  website: 'importContacts.website',
  linkedin: 'importContacts.file.linkedin',
  instagram: 'importContacts.file.instagram',
  facebook: 'importContacts.file.facebook',
  twitter: 'importContacts.file.twitter',
  notes: 'importContacts.file.notes',
}

const FIELDS = Object.keys(FIELD_LABEL_KEYS) as CanonicalField[]

const PREVIEW_ROWS = 3
const SHOWN_FAILURES = 10
const EXISTING_PAGE_SIZE = 200

interface ImportFailure {
  row: number
  name: string
  reason: string
}

interface ImportResult {
  created: number
  // Rows with no name, e-mail or phone.
  skipped: number
  duplicates: number
  // Rows left out because the plan limit was reached.
  notImported: number
  failures: ImportFailure[]
}

const EMPTY_RESULT: ImportResult = { created: 0, skipped: 0, duplicates: 0, notImported: 0, failures: [] }

type GhostPayload = NonNullable<ReturnType<typeof rowToGhostProfile>>

const displayName = (p: GhostPayload): string =>
  [p.firstName, p.lastName].filter(Boolean).join(' ') || p.contacts?.email || p.contacts?.phone || ''

// What identifies the contacts without account the user already has. When the
// list cannot be read the import goes on without the duplicate check.
const loadExistingKeys = async (): Promise<Set<string>> => {
  const keys = new Set<string>()
  try {
    for (let page = 1; ; page++) {
      const { data } = await api.get('/ghost-profiles', { params: { page, limit: EXISTING_PAGE_SIZE } })
      const profiles: any[] = data?.data ?? []
      profiles.forEach((profile) => identityKeys(profile).forEach((key) => keys.add(key)))
      if (profiles.length === 0 || page * EXISTING_PAGE_SIZE >= (data?.metadata?.count ?? 0)) break
    }
  } catch {
    // Keep what was read so far.
  }
  return keys
}

export const FileImportSection: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedFile | null>(null)
  // Which field each file column feeds: detected, then corrected by the user.
  const [mapping, setMapping] = useState<(CanonicalField | null)[]>([])
  const [fileName, setFileName] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [result, setResult] = useState<ImportResult>(EMPTY_RESULT)

  const previewPayloads = parsed
    ? (parsed.rows.map((r) => rowToGhostProfile(r, mapping)).filter(Boolean) as GhostPayload[])
    : []
  const validRowCount = previewPayloads.length
  const detectedFields = mapping.filter(Boolean) as CanonicalField[]

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)
      const name = (file.name || '').toLowerCase()
      const isCsv = name.endsWith('.csv') || file.type === 'text/csv'
      if (!isCsv) {
        setError(t('importContacts.file.unsupportedFormat'))
        return
      }
      try {
        const result = await parseContactsFile(file)
        if (result.rows.length === 0) {
          setError(t('importContacts.file.empty'))
          return
        }
        setParsed(result)
        setMapping(result.mapping)
        setFileName(file.name)
        setPhase('parsed')
      } catch (e: any) {
        // Map the parser's "unsupported format" sentinel to its translated copy;
        // any other failure falls back to the generic parse error.
        const key =
          e?.message === UNSUPPORTED_FORMAT_ERROR
            ? 'importContacts.file.unsupportedFormat'
            : 'importContacts.file.parseError'
        setError(t(key))
      }
    },
    [t],
  )

  const handleImport = useCallback(async () => {
    if (!parsed) return
    setPhase('importing')
    setProgress({ done: 0, total: 0 })

    // Leave out the contacts already there, and those twice in the file.
    const known = await loadExistingKeys()
    const toCreate: { row: number; payload: GhostPayload }[] = []
    let skipped = 0
    let duplicates = 0
    parsed.rows.forEach((r, index) => {
      const payload = rowToGhostProfile(r, mapping)
      if (!payload) {
        skipped++
        return
      }
      const keys = identityKeys(payload)
      if (keys.some((key) => known.has(key))) {
        duplicates++
        return
      }
      keys.forEach((key) => known.add(key))
      toCreate.push({ row: index + 1, payload })
    })
    setProgress({ done: 0, total: toCreate.length })

    let created = 0
    let notImported = 0
    const failures: ImportFailure[] = []
    for (let i = 0; i < toCreate.length; i++) {
      const { row, payload } = toCreate[i]
      try {
        await createGhostProfile(payload)
        created++
      } catch (e) {
        // The plan limit refuses every following row too: stop here, so the
        // Premium window opens once and not once per row.
        if (isPlanLimitError(e)) {
          notImported = toCreate.length - i
          break
        }
        failures.push({ row, name: displayName(payload), reason: translateApiError(t, e).message })
      }
      setProgress({ done: i + 1, total: toCreate.length })
    }

    await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    setResult({ created, skipped, duplicates, notImported, failures })
    setPhase('done')
    if (created > 0) {
      showSuccessToast(t('toast.success'), t('importContacts.file.importedToast', { count: created }))
    }
  }, [parsed, mapping, queryClient, t])

  const reset = () => {
    setPhase('idle')
    setParsed(null)
    setMapping([])
    setFileName('')
    setError(null)
    setProgress({ done: 0, total: 0 })
    setResult(EMPTY_RESULT)
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.titleRow}>
        <Icon name="upload" size={theme.iconSize.medium} color={theme.colors.primary} />
        <Text type="bodyBold" color={theme.colors.black}>
          {t('importContacts.file.title')}
        </Text>
      </View>
      <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.subtitle}>
        {t('importContacts.file.subtitle')}
      </Text>

      {/* Hidden native file input */}
      <input
        ref={fileInputRef as any}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={(e: any) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ''
        }}
      />

      {phase === 'idle' && (
        <div
          onDragOver={(e: any) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e: any) => {
            e.preventDefault()
            setIsDragging(false)
            const file = e.dataTransfer.files?.[0]
            if (file) handleFile(file)
          }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            borderRadius: 16,
            border: `1px dashed ${(theme.colors as any).primary}`,
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            backgroundColor: isDragging ? 'rgba(0,0,0,0.03)' : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <Icon name="uploadCloud" size={64} color={theme.colors.lightGrey} />
          <Text type="body" color={theme.colors.lightGrey}>{t('importContacts.file.drag')}</Text>
          <Button title={t('importContacts.selectFiles')} unfilled isSmall onPress={() => fileInputRef.current?.click()} />
          <Text type="bodySmall" color={theme.colors.lightGrey}>{t('importContacts.file.formats')}</Text>
        </div>
      )}

      {phase === 'parsed' && parsed && (
        <View style={styles.card}>
          <View style={styles.fileRow}>
            <Icon name="upload" size={theme.iconSize.small} color={theme.colors.primary} />
            <Text type="bodySmallBold" color={theme.colors.black}>{fileName}</Text>
          </View>
          <Text type="body" color={theme.colors.primary} style={styles.count}>
            {t('importContacts.file.detected', { count: validRowCount })}
          </Text>

          <Text type="bodySmall" color={theme.colors.lightGrey}>{t('importContacts.file.columnsDetected')}</Text>
          <View style={styles.chips}>
            {detectedFields.map((f) => (
              <View key={f} style={styles.chip}>
                <Icon name="check" size={theme.iconSize.extraSmall} color={theme.colors.green} />
                <Text type="bodyExtraSmall" color={theme.colors.black}>{t(FIELD_LABEL_KEYS[f])}</Text>
              </View>
            ))}
          </View>

          <View style={styles.columns}>
            <Text type="bodySmallBold" color={theme.colors.black}>{t('importContacts.file.columnsTitle')}</Text>
            {parsed.headers.map((header, i) => {
              const sample = parsed.rows.map((r) => r[i]).find(Boolean)
              return (
                <View key={i} style={styles.columnRow}>
                  <View style={styles.columnName}>
                    <Text type="bodySmall" color={theme.colors.black} numberOfLines={1}>{header}</Text>
                    {!!sample && (
                      <Text type="bodyExtraSmall" color={theme.colors.lightGrey} numberOfLines={1}>
                        {t('importContacts.file.sample', { value: sample })}
                      </Text>
                    )}
                  </View>
                  <select
                    data-testid={`csv-column-${i}`}
                    aria-label={header}
                    value={mapping[i] ?? ''}
                    onChange={(e: any) => {
                      const field = (e.target.value || null) as CanonicalField | null
                      setMapping((current) => setMappingAt(current, i, field))
                    }}
                    style={{
                      width: '45%',
                      maxWidth: 240,
                      padding: 6,
                      borderRadius: 8,
                      border: `1px solid ${(theme.colors as any).grey10}`,
                      backgroundColor: (theme.colors as any).white,
                      color: (theme.colors as any).black,
                      // Same font as the texts around (a select does not take it by itself).
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      fontSize: 14,
                    }}
                  >
                    <option value="">{t('importContacts.file.ignoreColumn')}</option>
                    {FIELDS.map((f) => (
                      <option key={f} value={f}>{t(FIELD_LABEL_KEYS[f])}</option>
                    ))}
                  </select>
                </View>
              )
            })}
          </View>

          {previewPayloads.length > 0 && (
            <View style={styles.columns}>
              <Text type="bodySmallBold" color={theme.colors.black}>{t('importContacts.file.previewTitle')}</Text>
              {previewPayloads.slice(0, PREVIEW_ROWS).map((p, i) => (
                <Text key={i} type="bodySmall" color={theme.colors.black} testID={`csv-preview-${i}`}>
                  {[
                    [p.firstName, p.lastName].filter(Boolean).join(' '),
                    p.contacts?.email,
                    p.contacts?.phone,
                    p.company,
                  ]
                    .filter(Boolean)
                    .join(' — ')}
                </Text>
              ))}
            </View>
          )}

          {validRowCount === 0 && (
            <Text type="bodySmall" color={theme.colors.error} style={styles.subtitle}>
              {t('importContacts.file.noUsableRows')}
            </Text>
          )}

          <View style={styles.actions}>
            <Button
              title={t('importContacts.file.importButton', { count: validRowCount })}
              onPress={handleImport}
              isSmall
              disabled={validRowCount === 0}
            />
            <Button title={t('common.cancel')} onPress={reset} unfilled isSmall />
          </View>
        </View>
      )}

      {phase === 'importing' && (
        <View style={styles.card}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text type="body" color={theme.colors.black} style={styles.center}>
            {t('importContacts.file.importing', { done: progress.done, total: progress.total })}
          </Text>
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressFill,
                { width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` },
              ]}
            />
          </View>
        </View>
      )}

      {phase === 'done' && (
        <View style={styles.card}>
          <Icon name="check" size={48} color={theme.colors.green} />
          <Text type="bodyBold" color={theme.colors.black} style={styles.center}>
            {t('importContacts.file.doneTitle', { count: result.created })}
          </Text>
          {result.duplicates > 0 && (
            <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.center}>
              {t('importContacts.file.doneDuplicates', { count: result.duplicates })}
            </Text>
          )}
          {result.skipped > 0 && (
            <Text type="bodySmall" color={theme.colors.lightGrey} style={styles.center}>
              {t('importContacts.file.doneIgnored', { count: result.skipped })}
            </Text>
          )}
          {result.notImported > 0 && (
            <Text type="bodySmall" color={theme.colors.error} style={styles.center}>
              {t('importContacts.file.doneLimit', { count: result.notImported })}
            </Text>
          )}
          {result.failures.length > 0 && (
            <View style={styles.columns}>
              <Text type="bodySmall" color={theme.colors.error}>
                {t('importContacts.file.doneFailed', { count: result.failures.length })}
              </Text>
              {result.failures.slice(0, SHOWN_FAILURES).map((f) => (
                <Text key={f.row} type="bodySmall" color={theme.colors.black} testID={`csv-failure-${f.row}`}>
                  {t('importContacts.file.failureLine', { row: f.row, name: f.name, reason: f.reason })}
                </Text>
              ))}
              {result.failures.length > SHOWN_FAILURES && (
                <Text type="bodySmall" color={theme.colors.lightGrey}>
                  {t('importContacts.file.moreFailures', { count: result.failures.length - SHOWN_FAILURES })}
                </Text>
              )}
            </View>
          )}
          <Button title={t('importContacts.file.importAnother')} onPress={reset} unfilled isSmall />
        </View>
      )}

      {error && <Text type="bodySmall" color={theme.colors.error} style={styles.subtitle}>{error}</Text>}
    </View>
  )
}

const getStyles = (theme: Theme) => ({
  wrapper: {
    width: '100%' as const,
    maxWidth: 860,
    alignSelf: 'center' as const,
    marginTop: theme.spacing.xxl,
    gap: theme.spacing.sm,
  },
  titleRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.sm,
  },
  subtitle: { marginBottom: theme.spacing.sm },
  card: {
    borderRadius: theme.border.large,
    borderWidth: 1,
    borderColor: theme.colors.grey10,
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    alignItems: 'center' as const,
  },
  fileRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.sm,
  },
  count: { marginTop: -theme.spacing.xs },
  chips: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: theme.spacing.sm,
    justifyContent: 'center' as const,
  },
  chip: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: theme.spacing.xs,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    borderRadius: theme.border.small,
    backgroundColor: theme.colors.grey10,
  },
  columns: {
    width: '100%' as const,
    gap: theme.spacing.xs,
  },
  columnRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: theme.spacing.sm,
  },
  columnName: { flex: 1, minWidth: 0 },
  actions: {
    flexDirection: 'row' as const,
    gap: theme.spacing.md,
    marginTop: theme.spacing.xs,
  },
  center: { textAlign: 'center' as const },
  progressTrack: {
    width: '100%' as const,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.grey10,
    overflow: 'hidden' as const,
  },
  progressFill: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.primary,
  },
})
